/**
 * Copyright (c) 2022 Yoshinobu Obata
 * Released under the Apache-2.0 license
 * LICENSE: http://www.apache.org/licenses/LICENSE-2.0
 */
const log = require('../../util/log');
const { detectLanguage, waitForModelReady } = require('./utils');

class EmotionModule {
    constructor() {
        this._classifier = null;
        this._status = 'not loaded';
        this._lastResult = null;
        this._emotionLabels = ['joy', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
    }

    getStatus() {
        return this._status;
    }

    async initialize() {
        if (this._classifier || this._status === 'loading') {
            await waitForModelReady(() => this._status);
            return this._classifier;
        }
        
        this._status = 'loading';
        try {
            const { pipeline } = window.transformers;
            
            log.log('Loading emotion classification model...');
            this._classifier = await pipeline(
                'zero-shot-classification',
                'Xenova/mobilebert-uncased-mnli',
                {
                    dtype: 'q8',
                    device: 'wasm'
                }
            );
            
            this._status = 'ready';
            log.log('Emotion model loaded successfully');
            alert("感情分析モデルの読み込みが完了しました。");
            return this._classifier;
        } catch (error) {
            this._status = 'error';
            log.error('Failed to initialize emotion model:', error);
            throw error;
        }
    }

    /**
     * 感情を分析
     * @param {string} text - 分析するテキスト
     * @param {Object|null} translationModule - 翻訳モジュール（共有または内部）
     */
    async analyze(text, translationModule = null) {
        if (!text || text.trim() === '') {
            return 'neutral';
        }

        try {
            await this.initialize();
            
            const lang = detectLanguage(text);
            let textForAnalysis = text;
            
            // 日本語の場合は英語に翻訳（翻訳モジュールがあれば）
            if (lang === 'japanese' && translationModule) {
                try {
                    textForAnalysis = await translationModule.translateToEnglish(text);
                } catch (error) {
                    log.warn('Translation failed, using original text:', error);
                }
            }
            
            const result = await this._classifier(textForAnalysis, this._emotionLabels);
            
            this._lastResult = {
                originalText: text,
                translatedText: textForAnalysis,
                result: result
            };
            
            return result.labels[0];
            
        } catch (error) {
            log.error('Emotion analysis error:', error);
            return 'error';
        }
    }

    /**
     * 特定の感情のスコアを取得
     * @param {string} text - 分析するテキスト
     * @param {string} targetEmotion - 取得したい感情
     * @param {Object|null} translationModule - 翻訳モジュール（共有または内部）
     */
    async getScore(text, targetEmotion, translationModule = null) {
        if (!text || text.trim() === '') {
            return 0;
        }

        try {
            await this.initialize();
            
            let result;
            if (this._lastResult && this._lastResult.originalText === text) {
                result = this._lastResult.result;
            } else {
                const lang = detectLanguage(text);
                let textForAnalysis = text;
                
                if (lang === 'japanese' && translationModule) {
                    try {
                        textForAnalysis = await translationModule.translateToEnglish(text);
                    } catch (error) {
                        log.warn('Translation failed, using original text:', error);
                    }
                }
                
                result = await this._classifier(textForAnalysis, this._emotionLabels);
                
                this._lastResult = {
                    originalText: text,
                    translatedText: textForAnalysis,
                    result: result
                };
            }
            
            const index = result.labels.indexOf(targetEmotion);
            if (index !== -1) {
                return Math.round(result.scores[index] * 100);
            }
            
            return 0;
            
        } catch (error) {
            log.error('Emotion score error:', error);
            return 0;
        }
    }
}

module.exports = EmotionModule;
